import { fetchAmberdataSeriesList } from '@/features/chart/lib/series-sources/amberdata-series'
import { normalizeBarTimeSec } from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  type ChartSeriesSource,
  SERIES_MIN_TIMEFRAME_SECONDS,
  type SeriesPoint,
} from '@/lib/tradingview/datafeeds/series-sources'
import { AnalyticsType } from '@/types/generated/common/types'
import type { SkewConstantList } from '@/types/generated/public_api'

export const SKEW_DELTAS = [5, 10, 15, 20, 25, 30, 35, 40, 45] as const
export type SkewDelta = (typeof SKEW_DELTAS)[number]

/** Constant-tenor target: the row nearest 30 days to expiration. 25Δ is the
 *  skew "backbone" (see the skew data-widget's DELTA_BUCKETS defaults). */
export const SKEW_TARGET_DTE_DAYS = 30

/** One point per timestamp: skew25 of the constant-tenor row nearest the
 *  30d target (the response carries several tenors per timestamp). */
export function skewConstantListToSeriesPoints(
  list: SkewConstantList | undefined,
  delta: SkewDelta = 25
): SeriesPoint[] {
  if (!list) return []
  const bestByTime = new Map<number, { dteDistance: number; skew25: number }>()
  for (const item of list.items) {
    const timeSec = normalizeBarTimeSec(Number(item.timestampMs))
    const skew25 = Number(item[`skew${String(delta).padStart(2, '0')}` as keyof typeof item])
    const dte = Number(item.daysToExpiration)
    if (!Number.isFinite(timeSec) || !Number.isFinite(skew25) || !Number.isFinite(dte)) continue
    // Log-time distance: vol term structure scales with sqrt(T) (variance with T),
    // so tenors compare naturally on a log axis — 2x and 0.5x the target tenor are
    // equidistant, unlike a raw day-count difference.
    const dteDistance = Math.abs(Math.log(dte / SKEW_TARGET_DTE_DAYS))
    if (!Number.isFinite(dteDistance)) continue
    const existing = bestByTime.get(timeSec)
    if (!existing || dteDistance < existing.dteDistance) {
      bestByTime.set(timeSec, { dteDistance, skew25 })
    }
  }
  return [...bestByTime.entries()]
    .sort(([a], [b]) => a - b)
    .map(([timeSec, { skew25 }]) => ({ timeSec, values: { optionsSkew25d: skew25 } }))
}

export const skewSeriesSource: ChartSeriesSource = {
  id: 'skew',
  metrics: ['optionsSkew25d'],
  refresh: 'bar-close',
  minTimeframeSeconds: SERIES_MIN_TIMEFRAME_SECONDS,
  // Skew is computed on read from the delta surface — coarse, never streamed.
  carryForward: true,
  fetchHistory: async (ctx, signal) => {
    const requestedDelta = Number(ctx.inputs?.delta)
    const delta = SKEW_DELTAS.includes(requestedDelta as SkewDelta)
      ? (requestedDelta as SkewDelta)
      : 25
    const list = await fetchAmberdataSeriesList(
      ctx,
      AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT,
      signal
    )
    return skewConstantListToSeriesPoints(list as SkewConstantList | undefined, delta)
  },
}
